(ns hypnos.sleeplab.core.patient
  "Core function for patient."
  (:require [hypnos.config :as config]
            [hypnos.graphql.queries :as queries]
            [hypnos.time :as t]
            [hypnos.translator.consult :as t-c]
            [hypnos.translator.patient :as t-p]
            [medley.core :as mc]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- comp-last-update-dt-desc
  [dt1 dt2]
  (t/before? dt2 dt1))

(defn last-completed-consult
  "Returns the last completed result for the patient."
  [patient]
  (let [consults        (:consult/_patient patient)
        sorted-consults (sort-by :consult/recorded-at (comp comp-last-update-dt-desc) consults)]
    (first (filter #(= :consult-state/completed (:consult/status %)) sorted-consults))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; API

(defn new-patient?
  "Returns `true` if state presents a new patient, otherwise `false`."
  [state]
  (and (:patient-looked-up? @state)
       (:new-patient? @state)))

(defn exchange?
  "Returns `true` if the service type should an exchange, otherwise `false`."
  [patient]
  (let [exchange-cut-off          (t/plus (t/now) (t/days config/EXCHANGE-DAYS))
        last-completed-consult    (last-completed-consult patient)
        last-completed-consult-on (:consult/recorded-at last-completed-consult)]
    (and last-completed-consult-on
         (not (t/before? last-completed-consult-on exchange-cut-off)))))

(defn enable-service-type?
  "Returns `true` if the service type should be enabled, otherwise `false`"
  [service-type patient]
  (let [exchange-cut-off          (t/plus (t/now) (t/days config/EXCHANGE-DAYS))
        last-completed-consult    (last-completed-consult patient)
        last-completed-consult-on (:consult/recorded-at last-completed-consult)]
    (cond
      ;; disable :service-type/new-setup
      (and (= :service-type/new-setup service-type)
           last-completed-consult-on)
      false
      ;; disable :service-type/exchange
      (and (= :service-type/exchange service-type)
           (or (nil? last-completed-consult-on)
               (t/before? last-completed-consult-on exchange-cut-off)))
      false
      :else true)))

(defn merge-patient-into-state
  "Merge the patient into the state and set the following (state) flags to `true`:

    * `:patient-looked-up?`
  "
  [state patient catalog]
  (when (and patient
             (nil? (get-in @state [:patient :patient/id :value])))
    (swap! state mc/deep-merge
           (t-p/patient->state catalog patient)
           {:new-patient?       false
            :patient-looked-up? true})))

(defn setup-new-consult
  "Setup a new consult in the state."
  [state patient]
  (let [exchange?              (exchange? patient)
        last-completed-consult (last-completed-consult patient)]
    (when (and (:new-consult? @state)
               exchange?)
      (let [patient-id (:patient/id patient)
            _          (rf/dispatch [:amplify/query
                                     queries/get-recommendations
                                     {:patient_id (str patient-id)}
                                     :sleeplab/set-recommendations-with-note
                                     :sleeplab/error])]
        ;; check if it’s a therapist-assisted consult with updated anatomy data
        (swap! state assoc-in [:patient :patient/last-consult] (:consult (t-c/consult->state last-completed-consult)))
        (swap! state assoc-in [:patient :patient/last-consult :consult/id :value] nil)
        (swap! state assoc-in [:patient :patient/last-consult :consult/mask :value] nil)
        (swap! state assoc-in [:patient :patient/last-consult :consult/selector :value] nil)
        (swap! state assoc-in [:patient :patient/last-consult :consult/selector-type :value] nil)
        (swap! state assoc-in [:patient :patient/last-consult :consult/service-type :value] :service-type/exchange)
        (swap! state assoc-in [:patient :patient/last-consult :consult/status :value] :consult-state/pending)
        (swap! state assoc :new-exchange? true)))))
